import { DEBOUNCE_DELAY } from '@autone/utils';
import { forwardRef, useCallback, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

import { NumberInput, type NumberInputProps } from './NumberInput';

export interface NumberInputWithDebounceProps extends NumberInputProps {
  onDebouncedChange?: (value: number | null) => void;
  debounceDelay?: number;
}

export const NumberInputWithDebounce = forwardRef<
  HTMLInputElement,
  NumberInputWithDebounceProps
>(
  (
    { onDebouncedChange, debounceDelay = DEBOUNCE_DELAY, onChange, ...props },
    ref,
  ) => {
    const [value, setValue] = useState<number | null>(
      props.defaultValue ?? null,
    );

    const debouncedCallback = useDebounceCallback((newValue: number | null) => {
      if (onDebouncedChange) {
        onDebouncedChange(newValue);
      }
    }, debounceDelay);

    const handleChange = useCallback(
      (newValue: number | null) => {
        setValue(newValue);
        if (onChange) {
          onChange(newValue);
        }
        debouncedCallback(newValue);
      },
      [onChange, debouncedCallback],
    );

    return (
      <NumberInput {...props} value={value} onChange={handleChange} ref={ref} />
    );
  },
);

NumberInputWithDebounce.displayName = 'NumberInputWithDebounce';
